import React, { useCallback, useRef, useState } from 'react';
import VideoInterval from './VideoInterval'
import trackGa from '../../analytics'
import { secondsToTime } from '../../utils'
import shaka from 'shaka-player/dist/shaka-player.ui'
import ResumePopup from '../ResumePopup'
import 'shaka-player/dist/controls.css'
import { useSelector } from 'react-redux';

/**
 * A React component for shaka-player.
 * @param {string} src
 * @param {shaka.extern.PlayerConfiguration} config
 * @param {boolean} autoPlay
 * @param {number} width
 * @param {number} height
 * @param ref
 * @returns {*}
 * @constructor
 */
function ShakaPlayer({
  videoInfo,
  src,
  config,
  autoPlay,
  width,
  height,
  handlePlayNext,
  handleUpdateHistory,
  setShouldGetHistory,
  historyData,
  autoplay = false,
  handleEndHistory,
}, ref) {
  const [showResumePopup, setShowResumePopup] = useState(false)
  const [watchedLength, setWatchedLength] = useState()
  const [runInterval, setRunInterval] = useState(false)
  const uiContainerRef = useRef(null);
  const videoRef = useRef(null);
  const controller = useRef({});
  const pausing = useRef()
  const user = useSelector(state => state.auth.user)

  const updateHistoryCallback = useCallback(() => {
    const { videoElement } = controller.current;
    handleUpdateHistory(
      videoElement.currentTime,
      videoElement.duration
    )
  }, [handleUpdateHistory])

  const handleResume = useCallback(() => {
    const { videoElement } = controller.current;
    setShowResumePopup(false)
    videoElement.currentTime = watchedLength
    videoElement.play()
  }, [watchedLength])

  const handleStartOver = useCallback(() => {
    const { videoElement } = controller.current;
    setShowResumePopup(false)
    videoElement.currentTime = 0
    videoElement.play()
  }, [])

  const handleMediaStart = useCallback(
    e => {
      console.log('video started', e)
      setRunInterval(true)

      if (pausing.current) {
        console.log('resume')
        pausing.current = false

        const paramsGA = {
          contentType: videoInfo.type,
          videoID: videoInfo.id
        }
        trackGa('MediaPlayback', 'mediaResume', paramsGA, user)
      }
    },
    [videoInfo, user]
  )

  const handleMediaPause = useCallback(() => {
    pausing.current = true
    setRunInterval(false)

    const paramsGA = {
      contentType: videoInfo.type,
      videoID: videoInfo.id
    }
    trackGa('MediaPlayback', 'mediaPause', paramsGA, user)
  }, [videoInfo, user])

  const handleDurationChange = useCallback(() => {
    const { videoElement } = controller.current;
    if (historyData && historyData.mediaId === videoInfo.id) {
      const mediaDuration = videoElement.duration
      const historyPercentage = historyData.position.percentage
      const historyPosition = (mediaDuration * historyPercentage) / 100

      if (!isNaN(historyPosition)) {
        if (videoElement.currentTime > historyPosition) return

        //force start over
        if (historyPercentage <= 5 || historyPercentage >= 95) {
          return setWatchedLength(0)
        }

        setWatchedLength(historyPosition)
        videoElement.pause()
        return setShowResumePopup(true)
      }
    } else {
      videoElement.play()
    }
  }, [historyData, videoInfo])

  const handleMediaEnd = useCallback(
    async e => {
      const { videoElement } = controller.current;
      const durationInSecond = videoInfo.duration * 60

      const paramsGA = {
        contentType: videoInfo.type,
        videoID: videoInfo.id
      }
      trackGa('MediaPlayback', 'mediaEnded', paramsGA, user)

      // send last history when duration greater than position
      handleEndHistory && handleEndHistory(
        durationInSecond,
        videoElement.currentTime,
        videoElement.duration
      )

      if (!handlePlayNext) {
        setRunInterval(false)
      }

      const canPlayNext = await handlePlayNext()
      if (!canPlayNext) {
        setRunInterval(false)
      }
    },
    [handlePlayNext, videoInfo, handleEndHistory, user]
  )

  const handleMediaSeek = useCallback(() => {
    const { videoElement } = controller.current;

    const paramsGA = {
      contentType: videoInfo.type,
      videoID: videoInfo.id,
      elapsedTime: videoElement.currentTime
    }
    trackGa('MediaPlayback', 'mediaSeek', paramsGA, user)
  }, [videoInfo, user])

  const handlerError = useCallback(err => {
    console.log(err)
  }, [])

  const handerLoaded = useCallback(event => {
    throw new Error(event)
  }, [])


  //EVENT LISTENERS
  const addEventListener = useCallback(async () => {
    const { videoElement } = controller.current;
    videoElement.addEventListener('play', handleMediaStart)
    videoElement.addEventListener('pause', handleMediaPause)
    videoElement.addEventListener('durationchange', handleDurationChange)
    videoElement.addEventListener('seeked', handleMediaSeek)
    videoElement.addEventListener('ended', handleMediaEnd)
  }, [
    handleMediaStart,
    handleMediaPause,
    handleDurationChange,
    handleMediaSeek,
    handleMediaEnd,
  ])

  const removeEventListener = useCallback(() => {
    const { videoElement } = controller.current;
    if (videoElement) {
      videoElement.removeEventListener('play', handleMediaStart)
      videoElement.removeEventListener('pause', handleMediaPause)
      videoElement.removeEventListener('durationchange', handleDurationChange)
      videoElement.removeEventListener('seeked', handleMediaSeek)
      videoElement.removeEventListener('ended', handleMediaEnd)
    }
  }, [
    handleMediaStart,
    handleMediaPause,
    handleDurationChange,
    handleMediaSeek,
    handleMediaEnd,
  ])


  // Effect to handle component mount & mount.
  // Not related to the src prop, this hook creates a shaka.Player instance.
  // This should always be the first effect to run.
  React.useEffect(() => {
    shaka.polyfill.installAll()
    if (!shaka.Player.isBrowserSupported()) {
      return false
    }
    const player = new shaka.Player(videoRef.current);
    const ui = new shaka.ui.Overlay(
      player,
      uiContainerRef.current,
      videoRef.current,
    );
    const controls = ui.getControls()

    // Store Shaka's API in order to expose it as a handle.
    controller.current = { player, ui, videoElement: videoRef.current, controls };

    player.addEventListener('error', handlerError)
    player.addEventListener('loaded', handerLoaded)
    player.setTextTrackVisibility(true)
  }, [handlerError, handerLoaded]);

  // Load the source url when we have one.
  React.useEffect(() => {
    const { player } = controller.current;
    if (player) {
      addEventListener()
      player.load(src);
    }
    return () => {
      player.destroy()
      removeEventListener()
    }
  }, [src, addEventListener, removeEventListener]);

  // Define a handle for easily referencing Shaka's player & ui API's.
  React.useImperativeHandle(ref, () => ({
    get player() {
      return controller.current.player;
    },
    get ui() {
      return controller.current.ui;
    },
    get videoElement() {
      return controller.current.videoElement;
    },
    get controls() {
      return controller.current.controls
    }
  }));

  return (
    <div ref={uiContainerRef}>
      {runInterval && <VideoInterval updateHistoryCallback={updateHistoryCallback} />}
      <video
        ref={videoRef}
        autoPlay={autoPlay}
        width={width}
        height={height}
        style={{ maxWidth: '100%', width: '100vw', height: '100vh' }}
      />
      {showResumePopup && (
        <ResumePopup
          watchedLength={secondsToTime(watchedLength)}
          onResume={handleResume}
          onStartOver={handleStartOver}
        />
      )}
    </div>
  );
}

export default React.memo(React.forwardRef(ShakaPlayer));